/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { Grid, Icon, TextGroupMedium } from "@cd2/components"
import React from "react"

import {
  BARSCAN_COLUMN_WIDTH,
  DISCOUNT_COLUMN_WIDTH,
  filterDeleted,
  IItem,
  IQuoteOrderRecord,
  itemTypes,
  LOCATION_COLUMN_WIDTH,
  MARGIN_COLUMN_WIDTH,
  PRICE_COLUMN_WIDTH,
  PRODUCT_SELECT_COLUMN_WIDTH,
  QUANTITY_COLUMN_WIDTH,
} from "./quoteOrderUtils"

import { Modal } from "@cd2/components/build/es6"
import { Form2 } from "@cd2/forms/build/es6"
import { ConfirmCancelButtons } from "../../Components/Buttons/ConfirmCancelButtons"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { WarehouseSelect } from "../../Components/SelectLists/WarehouseSelectList"
import { icons } from "../../constants/icons"
import { useConfirmation } from "../../hooks/useConfirmation"
import { useSettings } from "../../utils/useSettings"
import { useConfirmCancelConfirmation } from "../../hooks/useConfirmCancelConfirmation"

export function HeadingRow({
  onOverride,
  handleItemChange,
  useExplicitAllocations,
  useStandardPricing,
  quote,
  isCustomerViewing,
  hideMarginColumn,
  hidePriceColumn,
  setAutoAllocateMax,
}: {
  useExplicitAllocations: boolean
  useStandardPricing?: boolean
  onOverride: (data: { margin: number }) => void
  handleItemChange(itemId: string, newItem: IItem | ((oldItem: IItem) => IItem)): void
  quote: IQuoteOrderRecord
  isCustomerViewing?: boolean
  hideMarginColumn?: boolean
  hidePriceColumn?: boolean
  setAutoAllocateMax: (value: boolean) => void
}) {
  const spanCss = css`
    font-size: 12px;
  `
  const { currentUser } = useGlobalState()
  const { barScanOrders, showDiscountOption, canBulkAssignWarehouse, warehouses } = useSettings()

  const shouldShowDiscount = !!(showDiscountOption || quote.hasOpportunityCertificate)

  const [handleOverrideNode, handleOverride] = useConfirmation<{}, { margin: number } | false>(
    ({ onResolve }) => (
      <Modal
        render={() => (
          <Form2.Form onSubmit={onResolve}>
            <TextGroupMedium
              heading="Update all margins"
              body="Set all margins to the same value."
            />
            <Form2.Number prefix="%" field="margin" placeholder="33" />
            <ConfirmCancelButtons onConfirm="submit" onCancel={() => onResolve(false)} />
          </Form2.Form>
        )}
        onRequestClose={() => onResolve(false)}
        isOpen
      />
    ),
  )
  const [handleOverrideNodeDiscount, handleDiscountOverride] = useConfirmation<
    {},
    { margin: number } | false
  >(({ onResolve }) => (
    <Modal
      render={() => (
        <Form2.Form onSubmit={onResolve}>
          <TextGroupMedium
            heading="Update all discounts"
            body="Set all discounts to the same value."
          />
          <Form2.Number prefix="%" field="discountPercent" placeholder="33" />
          <ConfirmCancelButtons onConfirm="submit" onCancel={() => onResolve(false)} />
        </Form2.Form>
      )}
      onRequestClose={() => onResolve(false)}
      isOpen
    />
  ))
  const [bulkAssignWarehouse, handleBulkAssignWarehouse] = useConfirmation<
    void,
    { id: string } | false
  >(({ onResolve }) => {
    const [defaultWarehouse, setDefaultWarehouse] = React.useState<
      (typeof warehouses)[number] | null
    >(null)
    return (
      <Modal
        css={css`
          overflow: visible;
        `}
        render={() => (
          <Grid gap={8}>
            <TextGroupMedium
              heading="Update all locations"
              body="Set all locations to a specified warehouse."
            />
            <WarehouseSelect
              key={warehouses
                .map(x => x.id)
                .sort((a, b) => a.localeCompare(b))
                .join("_")}
              onChange={(_, warehouse) => setDefaultWarehouse(warehouse)}
              value={defaultWarehouse?.id}
              branchId={currentUser.branch?.id ?? undefined}
              filter={
                warehouses && warehouses.length > 0
                  ? { matches: { id: { or: warehouses.map(x => x.id) } } }
                  : undefined
              }
              isClearable
            />
            <ConfirmCancelButtons
              onConfirm={() => (defaultWarehouse ? onResolve(defaultWarehouse) : null)}
              onCancel={() => onResolve(false)}
            />
          </Grid>
        )}
        onRequestClose={() => onResolve(false)}
        isOpen
      />
    )
  })

  const [confirmBulkUnallocateNode, handleConfirmBulkUnallocate] = useConfirmCancelConfirmation({
    heading: "Unallocate all stock from all items?",
    body: "This will unallocate all stock from all items in the order. Are you sure you want to continue?",
  })

  async function handleOverrideMargin() {
    const data = await handleOverride({})
    if (data) {
      onOverride(data)
    }
  }
  async function handleOverrideDiscount() {
    const data = await handleDiscountOverride({})
    if (data) {
      onOverride(data)
    }
  }

  async function handleAssignLocations() {
    const data = await handleBulkAssignWarehouse()
    if (data) {
      quote.items.forEach(item => {
        handleItemChange(item.id, oldItem => {
          const match = oldItem.product?.product_locations.find(x => x.warehouse?.id === data.id)
          const current = oldItem.product_location
          return { ...oldItem, product_location: match ?? current }
        })
      })
    }
  }

  async function handleBulkUnallocateStock() {
    const confirmed = await handleConfirmBulkUnallocate()
    if (!confirmed) {
      return
    }
    const undeletedItems = filterDeleted(quote.items)
    undeletedItems.forEach(item => {
      if (item.type == itemTypes.text) {
        return
      }
      handleItemChange(item.id, oldItem => ({ ...oldItem, allocated_quantity: 0 }))
    })
    setAutoAllocateMax(false)
  }

  return (
    <React.Fragment>
      {handleOverrideNode}
      {handleOverrideNodeDiscount}
      {bulkAssignWarehouse}
      {confirmBulkUnallocateNode}
      <Grid
        columns={
          [
            PRODUCT_SELECT_COLUMN_WIDTH,
            barScanOrders && !quote.isInternalTransfer && BARSCAN_COLUMN_WIDTH,
            LOCATION_COLUMN_WIDTH,
            QUANTITY_COLUMN_WIDTH,
            useExplicitAllocations && QUANTITY_COLUMN_WIDTH,
            shouldShowDiscount && DISCOUNT_COLUMN_WIDTH,
            !isCustomerViewing && PRICE_COLUMN_WIDTH,
            !hidePriceColumn && !quote.isInternalTransfer && PRICE_COLUMN_WIDTH,
            quote.isInternalTransfer || hideMarginColumn
              ? null
              : useStandardPricing || !isCustomerViewing
              ? MARGIN_COLUMN_WIDTH
              : null,
          ].filter(Boolean) as any
        }
        css={css`
          font-weight: 600;
          font-size: 13px;
        `}
        gap={6}
      >
        <span css={spanCss}>Product</span>
        {barScanOrders && !quote.isInternalTransfer && <span css={spanCss}>Barscan</span>}
        {canBulkAssignWarehouse ? (
          <span css={css(spanCss, `cursor: pointer;`)} onClick={() => handleAssignLocations()}>
            Location <Icon icon={icons.edit} color="grey" />
          </span>
        ) : (
          <span css={spanCss}>Location</span>
        )}
        <span css={spanCss}>Qty</span>
        {useExplicitAllocations && (
          <span css={css(spanCss, `cursor: pointer;`)} onClick={() => handleBulkUnallocateStock()}>
            Alloc. Qty <Icon icon={icons.align} color="grey" />
          </span>
        )}
        {shouldShowDiscount && (
          <span css={spanCss} onClick={() => handleOverrideDiscount()}>
            Discount <Icon icon={icons.edit} color="grey" />
          </span>
        )}
        {!isCustomerViewing && (
          <React.Fragment>
            {useStandardPricing ? (
              <span css={spanCss}>Standard Price</span>
            ) : (
              <span css={spanCss}>Unit Cost</span>
            )}
          </React.Fragment>
        )}
        {!hidePriceColumn && !quote.isInternalTransfer && <span css={spanCss}>Price</span>}
        {hideMarginColumn || quote.isInternalTransfer ? null : useStandardPricing ? (
          <span css={spanCss}>Discount %</span>
        ) : isCustomerViewing ? null : (
          <React.Fragment>
            <span css={css(spanCss, `cursor: pointer;`)} onClick={() => handleOverrideMargin()}>
              Margin <Icon icon={icons.edit} color="grey" />
            </span>
          </React.Fragment>
        )}
      </Grid>
    </React.Fragment>
  )
}
